import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  pointsGetAllActiveUsersSuccess, pointsGetAllActiveUsersFail,
  addUserToPoints,
  matchDaySaved, matchDayNotSaved,
  sendEmailSuccess, sendEmailFailure
} from '../actions/points';
import { serverError } from '../actions/common';
import { getPoints } from '../reducers/points';
import { numberOfMatches } from '../domain/matchSchedule';
import { defaultParticipants } from '../domain/defaultParticipants';
import api from '../api';

/* Best effort: if the previous Match Day cannot be read, every active Player is
   pre-selected, which is what the wizard did before. Never blocks entry. */
export function* carryOverParticipants(active) {
  try {
    const list = yield call([api, api.matchDays], { limit: 1 });
    if (list.statusCode !== 200 || !list.mutableId) {
      return active;
    }
    const previous = yield call([api, api.matchDay], list.mutableId);
    if (previous.statusCode !== 200 || !previous.matchDay) {
      return active;
    }
    return defaultParticipants(active, previous.matchDay);
  } catch {
    return active;
  }
}

export function* getAllActiveUsers() {
  try {
    const response = yield call([api, api.activePlayers]);
    if (!response) {
      yield put(serverError(['Fetch failed']));
      return;
    }
    if (response.statusCode === 200) {
      yield put(pointsGetAllActiveUsersSuccess(response.users));
      const preselected = yield call(carryOverParticipants, response.users);
      for (let i = 0; i < preselected.length; i++) {
        yield put(addUserToPoints(preselected[i]));
      }
    } else {
      yield put(pointsGetAllActiveUsersFail());
    }
  } catch {
    yield put(serverError(['Fetch failed']));
  }
}
export function* watchGetAllUsers() {
  yield takeLatest('POINTS_GET_ALL_ACTIVE_USERS', getAllActiveUsers);
}
export function* matchDaySave() {
  const pointsState = yield select(getPoints);

  const matchDay = { date: new Date(pointsState.dateTime).toISOString() };
  const players = {};
  const playersNames = {};
  /* Deep clone array with objects :/ */
  const res = JSON.parse(JSON.stringify(pointsState.expandedResults));
  let matchError = false;
  for (let i = 0; i < res.length; i++) {
    if (res[i].player1Score + res[i].player2Score !== res[i].setsNumber) {
      matchError = true;
    }
    players[res[i].player1._id] = players[res[i].player1._id] + 1 || 1;
    players[res[i].player2._id] = players[res[i].player2._id] + 1 || 1;
    res[i].player1 = res[i].player1._id;
    res[i].player2 = res[i].player2._id;
    delete res[i].expanded;
    delete res[i]._id;
  }
  for (let i = 0; i < pointsState.users.length; i++) {
    playersNames[pointsState.users[i]._id] = pointsState.users[i];
  }

  // each player should have the same number of matches
  if (
    Object.keys(players).length !== pointsState.usersPoints.length
    ||
    Object
    .keys(players)
    .map(a => players[a])
    .reduce(
      (prev, val, ind, arr) => prev + ((ind + 1 < arr.length && val !== arr[ind + 1]) ? val : 0)
      , 0) > 0) {
    yield put(serverError(['Error with players number']));
  } else if (res.length !== numberOfMatches(Object.keys(players).length) || matchError) {
    yield put(serverError(['Match number is invalid']));
  } else {
    const penalties = [];
    const results = pointsState.users;
    for (let i = results.length - 1; i >= 0; i--) {
      if (Object.keys(results[i].penalties).length > 0) {
        penalties.push({ userId: results[i]._id, penalties: results[i].penalties });
      }
    }
    matchDay.comment = pointsState.comment;
    matchDay.results = res;
    matchDay.penalties = penalties;
    matchDay.players = Object.keys(players).map(ind => ({ userId: ind }));
    try {
      const matchDayStatus = pointsState.editingId
        ? yield call([api, api.updateMatchDay], pointsState.editingId, matchDay)
        : yield call([api, api.saveMatchDay], matchDay);
      if (matchDayStatus.statusCode === 200) {
        const usersResults = [];
        matchDayStatus.players.forEach(el => {
          usersResults.push({
            name: playersNames[el.userId].name,
            points: el.points,
            _id: el.userId
          });
        });
        const monthPoints = [];
        matchDayStatus.monthPoints.forEach(el => {
          monthPoints.push({
            name: playersNames[el._id].name,
            points: el.points,
            _id: el._id
          });
        });
        usersResults.sort((a, b) => {
          if (a.points > b.points) {
            return -1;
          } else if (a.points < b.points) {
            return 1;
          }
          return 0;
        });
        monthPoints.sort((a, b) => {
          if (a.points > b.points) {
            return -1;
          } else if (a.points < b.points) {
            return 1;
          }
          return 0;
        });
        yield put(matchDaySaved(usersResults, monthPoints));
      } else {
        yield put(matchDayNotSaved(matchDayStatus.messages || [matchDayStatus.message]));
      }
    } catch (e) {
      yield put(serverError([e.message]));
    }
  }
}
export function* watchMatchDaySave() {
  yield takeLatest('POINTS_SAVE_REQUEST', matchDaySave);
}
export function* sendEmail() {
  try {
    const response = yield call([api, api.sendDigest]);
    if (!response) {
      yield put(serverError(['Fetch failed']));
      return;
    }
    if (response.statusCode === 200) {
      yield put(sendEmailSuccess());
    } else {
      yield put(sendEmailFailure());
    }
  } catch {
    yield put(serverError(['Fetch failed']));
  }
}
export function* watchSendEmail() {
  yield takeLatest('ADMIN_POINTS_SEND_EMAIL', sendEmail);
}
export default function* pointsSaga() {
  yield all([
    fork(watchMatchDaySave),
    fork(watchGetAllUsers),
    fork(watchSendEmail)
  ]);
}
